import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { ConfigProvider, theme as antTheme } from 'antd';

type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  theme: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const THEME_KEY = 'budgetAppTheme';

// Red Bull brand tokens
const brandTokens = {
  colorPrimary: '#1B6AEE',
  colorSuccess: '#00A86B',
  colorWarning: '#E6A700',
  colorError: '#DB0A40',
  colorInfo: '#1B6AEE',
  borderRadius: 8,
  fontFamily: "var(--rb-font-body)",
  fontSize: 14,
  colorText: '#000F1E',
  colorTextSecondary: 'rgba(0, 15, 30, 0.6)',
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(() => {
    return (localStorage.getItem(THEME_KEY) as ThemeMode) || 'light';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    localStorage.setItem(THEME_KEY, mode);
  }, [mode]);

  const toggleTheme = useCallback(() => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme: mode, toggleTheme }}>
      <ConfigProvider
        theme={{
          algorithm: mode === 'dark' ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
          token: {
            ...brandTokens,
            colorBgContainer: mode === 'dark' ? '#1e293b' : '#FFFFFF',
            colorBgLayout: mode === 'dark' ? '#0f172a' : '#F8F8F8',
          },
          components: {
            Table: {
              headerBg: '#0F2A47',
              headerColor: '#FFFFFF',
            },
          },
        }}
      >
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
